import { apiClient } from '../ApiClient'
import { useAppStore } from '@/stores/appStore'
import type { SourceOfTruth, ContextResponse } from '@/types/api'
import type {
  ContextDomain,
  ContextChunkSummary,
  ContextConflict,
  ConflictResolutionAction,
  GapAnalysisResult,
  ToolTemplate,
  FormattedContext,
  ContextHistoryResult,
} from '@/types/contextEngine'

/**
 * LEGACY — Context Explorer v1 surface. Now calls the startup-scoped
 * `/v1/startups/{id}/context/*` endpoints, resolving the active startup from
 * appStore so the twelve call sites did not have to change. It previously hit
 * the unscoped scopedContext(`/*`) mount, which skipped requireStartupMembership
 * and derived tenancy from a token claim. Kept
 * callable because the Context Explorer pages (DomainDetail / Conflicts /
 * History / etc) and the contextEngineStore / useSourceOfTruth still depend
 * on them while their per-screen rewrites happen in Phase 2+.
 *
 * Do NOT reach for this in new code. The eslint `no-restricted-imports` rule
 * banning `@/services/legacy/*` enforces this — the only files allowed in
 * are the four legacy callers explicitly listed in the rule's allowlist.
 *
 * Method names are preserved verbatim from the prior in-place definitions
 * (commit 06 + earlier) so callers move with a one-line import change. The
 * `legacy*` prefix on `legacyGetSourceOfTruth` is also preserved — it's
 * the "this hits the unscoped path" signal at every call site.
 */
/**
 * Build a startup-scoped context path. The Explorer screens have no startupId
 * of their own, so it comes from the active startup the shell already
 * established.
 */
function scopedContext(path: string): string {
  const startupId = useAppStore.getState().startup?.id
  if (!startupId) {
    throw new Error('No active startup — cannot call the context API yet.')
  }
  return `/v1/startups/${encodeURIComponent(startupId)}/context${path}`
}

export class LegacyContextService {
  /** GET /v1/context/source-of-truth — superseded by `contextService.getSourceOfTruth(startupId, domain?)`. */
  async legacyGetSourceOfTruth(): Promise<SourceOfTruth> {
    const response = await apiClient.get<ContextResponse>(
      scopedContext('/source-of-truth')
    )
    return response.sourceOfTruth || {}
  }

  /** GET /v1/context/gaps — superseded by `contextService.getGaps(startupId, filters?)`. */
  async getGapAnalysis(): Promise<GapAnalysisResult> {
    return apiClient.get<GapAnalysisResult>(scopedContext('/gaps'))
  }

  /** POST /v1/context/analyze-gaps — server-side ingestion now triggers gap analysis. */
  async triggerGapAnalysis(): Promise<GapAnalysisResult> {
    return apiClient.post<GapAnalysisResult>(scopedContext('/analyze-gaps'), {})
  }

  /** POST /v1/context/gaps/{id}/infer — superseded by per-gap upload + fill flow. */
  async inferGap(
    gapId: string
  ): Promise<{ gapId: string; inferredText: string }> {
    return apiClient.post(
      scopedContext(`/gaps/${encodeURIComponent(gapId)}/infer`),
      {}
    )
  }

  /** GET /v1/context/chunks — chunks are an internal concept; do not surface. */
  async listChunks(
    domain?: ContextDomain
  ): Promise<{ chunks: ContextChunkSummary[]; total: number }> {
    const params = domain ? `?domain=${encodeURIComponent(domain)}` : ''
    return apiClient.get(scopedContext(`/chunks${params}`))
  }

  /** GET /v1/context/chunks/{id} — chunks are an internal concept; do not surface. */
  async getChunk(chunkId: string): Promise<ContextChunkSummary> {
    return apiClient.get(scopedContext(`/chunks/${encodeURIComponent(chunkId)}`))
  }

  /** PATCH /v1/context/chunks/{id} — chunks are an internal concept; do not surface. */
  async updateChunk(
    chunkId: string,
    updates: { domain?: ContextDomain; confidence?: number; text?: string }
  ): Promise<ContextChunkSummary> {
    return apiClient.patch(
      scopedContext(`/chunks/${encodeURIComponent(chunkId)}`),
      updates
    )
  }

  /** DELETE /v1/context/chunks/{id} — chunks are an internal concept; do not surface. */
  async archiveChunk(chunkId: string): Promise<void> {
    await apiClient.delete(scopedContext(`/chunks/${encodeURIComponent(chunkId)}`))
  }

  /** GET /v1/context/conflicts — conflicts now surface via SSE on the new shell. */
  async listConflicts(): Promise<{ conflicts: ContextConflict[] }> {
    return apiClient.get(scopedContext('/conflicts'))
  }

  /** POST /v1/context/conflicts/{id}/resolve — conflicts now surface via SSE on the new shell. */
  async resolveConflict(
    conflictId: string,
    action: ConflictResolutionAction,
    mergedText?: string
  ): Promise<void> {
    await apiClient.post(
      scopedContext(`/conflicts/${encodeURIComponent(conflictId)}/resolve`),
      {
        action,
        ...(mergedText ? { mergedText } : {}),
      }
    )
  }

  /** GET /v1/context/templates — moves onto the scoped /context/templates path. */
  async listTemplates(): Promise<{ templates: ToolTemplate[] }> {
    return apiClient.get(scopedContext('/templates'))
  }

  /** POST /v1/context/generate — superseded by the scoped /context/generate Export path. */
  async generateContext(
    templateId: string,
    operation: string,
    options?: { userInstructions?: string; targetAudience?: string }
  ): Promise<FormattedContext> {
    return apiClient.post(scopedContext('/generate'), {
      templateId,
      operation,
      ...options,
    })
  }

  /** GET /v1/context/history — history tracking is moving to the scoped path. */
  async listHistory(
    domain?: ContextDomain,
    limit?: number,
    cursor?: string
  ): Promise<ContextHistoryResult> {
    const params = new URLSearchParams()
    if (domain) params.set('domain', domain)
    if (limit) params.set('limit', String(limit))
    if (cursor) params.set('cursor', cursor)
    const qs = params.toString()
    return apiClient.get(scopedContext(`/history${qs ? `?${qs}` : ''}`))
  }

  /** POST /v1/context/history/{id}/revert — history tracking is moving to the scoped path. */
  async revertChange(changeId: string): Promise<void> {
    await apiClient.post(
      scopedContext(`/history/${encodeURIComponent(changeId)}/revert`),
      {}
    )
  }
}

export const legacyContextService = new LegacyContextService()
